import { Component, HostBinding, Input } from '@angular/core';

/**
 * Botão com carregamento no próprio botão (CU-86akkqppg).
 *
 * É o estado de carregamento de submit de formulário: em vez de overlay
 * cobrindo a tela por causa de um POST, o botão que disparou a ação desabilita
 * e gira. Aplica-se por atributo, então o botão que já existe mantém as classes
 * e o `(click)` dele:
 *
 * ```html
 * <button class="sf-btn sf-btn--primary" [appLoadingButton]="saving" (click)="save()">Salvar</button>
 * ```
 *
 * ## Modelo de botão
 *
 * Quem usa continua trazendo as próprias classes — a diretiva não pinta botão.
 * O que ela acrescenta é `is-loading`, que é o estado de carregamento do modelo
 * `sf-btn` (`src/_buttons.scss`): sem clique e sem sombra enquanto a ação corre.
 * O giro continua sendo o daqui, e o `_buttons.scss` desliga o `::after` dele
 * quando os dois estão no mesmo botão, para não girarem dois.
 *
 * Botão que ainda está no `btn btn-*` do Bootstrap não muda de nada: `is-loading`
 * sozinho não tem desenho, e o giro é o mesmo de sempre.
 *
 * ## Largura preservada
 *
 * O rótulo não sai do botão: ele continua ocupando o mesmo espaço e só fica
 * invisível (`visibility`), com o giro em cima, posicionado absoluto. Trocar o
 * rótulo pelo spinner mudaria a largura e o layout pularia a cada submit.
 *
 * O invólucro do conteúdo é `display: contents` para não virar um item de flex
 * a mais em botão que já é flex — sem caixa própria, ele some do layout e o
 * `visibility: hidden` chega ao conteúdo por herança.
 *
 * ## Movimento
 *
 * Aqui o giro não é enfeite: é o único aviso de que o clique foi ouvido. Sob
 * `prefers-reduced-motion` ele desacelera em vez de parar — parado seria um
 * botão desabilitado sem explicação.
 */
@Component({
  selector: 'button[appLoadingButton]',
  templateUrl: './loading-button.component.html',
  styleUrls: ['./loading-button.component.scss'],
})
export class LoadingButtonComponent {

  /** Se a ação do botão está em curso. */
  @Input('appLoadingButton')
  set appLoadingButton(value: boolean | string) {
    this.loading = coerceBoolean(value);
  }

  /**
   * Desabilitado por outro motivo — filtro vazio, formulário inválido. Fica
   * como input próprio para somar ao carregamento em vez de disputar o mesmo
   * atributo com quem usa o botão.
   */
  @Input()
  set disabled(value: boolean | string) {
    this.disabledByHost = coerceBoolean(value);
  }

  loading = false;
  private disabledByHost = false;

  @HostBinding('class.loading-button')
  readonly hostClass = true;

  /**
   * Estado de carregamento do modelo `sf-btn`. É o que tira o clique e a sombra
   * do botão; o giro em si continua sendo o `<span>` do template.
   */
  @HostBinding('class.is-loading')
  get loadingClass(): boolean {
    return this.loading;
  }

  @HostBinding('attr.disabled')
  get disabledAttribute(): string | null {
    return this.loading || this.disabledByHost ? '' : null;
  }

  @HostBinding('attr.aria-busy')
  get ariaBusy(): string | null {
    return this.loading ? 'true' : null;
  }
}

/**
 * Mesma coerção de atributo booleano do Angular: `disabled` escrito sem valor
 * chega como string vazia e precisa contar como verdadeiro, senão o atributo
 * estático seria apagado pelo host binding.
 */
function coerceBoolean(value: boolean | string): boolean {
  return value !== false && value !== null && value !== undefined && value !== 'false';
}
